import { useEffect, useRef, useState } from 'react';
export const useToggleVideoControlBar = (playerRef, playerReady, currentVideoIndex, playlist, videoContainerRef, adPlaying) => {
  const [showControls, setShowControls] = useState(true);
  const timeout = useRef(null);
  const unshowTimeout = () => {
    if (timeout.current) {
      window.clearTimeout(timeout.current);
      timeout.current = null;
    }
    timeout.current = window.setTimeout(() => setShowControls(false), 3000);
  };
  useEffect(() => {
    if (!adPlaying) {
      setShowControls(true);
      unshowTimeout();
    }
  }, [adPlaying]);

  // hide control-bar after 2 seconds when video starts to play
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady) {
      return;
    }
    player.on('play', unshowTimeout);
    return () => {
      if (player) {
        player.off('play', unshowTimeout);
      }
    };
  }, [playerReady]);

  // show control-bar after last video ended
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady) {
      return;
    }
    const handleEnded = () => {
      setShowControls(true);
    };
    if (currentVideoIndex === playlist.length() - 1) {
      player.on('ended', handleEnded);
    }
    return () => {
      if (player) {
        player.off('ended', handleEnded);
      }
    };
  }, [currentVideoIndex, playerReady, playlist]);

  // hide control-bar when user is inactive and video is playing
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady) {
      return;
    }
    const handleUserInactive = () => {
      setShowControls(false);
    };
    player.on('userinactive', handleUserInactive);
    return () => {
      if (player) {
        player.off('userinactive', handleUserInactive);
      }
    };
  }, [playerReady]);

  // show control bar on mouse entering and moving inside player
  useEffect(() => {
    const videoContainer = videoContainerRef.current;
    if (!videoContainer) {
      return;
    }
    const handleMouseEvent = () => {
      setShowControls(true);
    };
    videoContainer.addEventListener('mouseenter', handleMouseEvent);
    videoContainer.addEventListener('mousemove', handleMouseEvent);
    return () => {
      if (videoContainer) {
        videoContainer.removeEventListener('mouseenter', handleMouseEvent);
        videoContainer.removeEventListener('mousemove', handleMouseEvent);
      }
    };
  }, []);

  // show controls on touchend for tablets
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady) {
      return;
    }
    const handleTouchEnd = () => {
      if (!player.ads.inAdBreak()) {
        setShowControls(true);
        unshowTimeout();
      }
    };
    player.on('touchend', handleTouchEnd);
    return () => {
      if (player) {
        player.off('touchend', handleTouchEnd);
      }
    };
  }, [playerReady]);

  // show/hide control-bar by space bar click
  useEffect(() => {
    const handleKeyDown = event => {
      if (event.keyCode === 32) {
        setShowControls(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  useEffect(() => {
    return () => {
      if (timeout.current) {
        window.clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, []);
  return {
    showControls,
    setShowControls
  };
};